import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useProject } from '../context/ProjectContext';
import { getApiUrl, axiosConfig } from '../config/api';
import UpgradeModal from '../components/UpgradeModal';
import './Dashboard.css';
import '../components/PageLoader.css';

// SEO and website fun facts
const funFacts = [
  "Did you know? Google processes over 8.5 billion searches per day!",
  "Did you know? 75% of users never scroll past the first page of search results.",
  "Did you know? Websites that load in 2 seconds have a 9% bounce rate, while those taking 5 seconds have a 38% bounce rate.",
  "Did you know? Mobile-first indexing means Google primarily uses the mobile version of your site for ranking.",
  "Did you know? Pages with images get 94% more views than those without.",
  "Did you know? The average user reads only 20% of the text on a webpage.",
  "Did you know? Websites with SSL certificates (HTTPS) rank higher in Google search results.",
  "Did you know? The first 3 seconds of page load time impact 53% of mobile site visits.",
  "Did you know? Internal linking can improve your site's SEO and help users navigate better.",
  "Did you know? Long-form content (2000+ words) tends to rank higher in search results.",
  "Did you know? Schema markup can help your website appear in rich snippets, increasing click-through rates.",
  "Did you know? Core Web Vitals are now a ranking factor for Google search results.",
  "Did you know? Websites with fast loading times have better conversion rates.",
  "Did you know? Alt text for images not only helps SEO but also improves accessibility.",
  "Did you know? A well-structured sitemap helps search engines crawl and index your site more effectively."
];

const Dashboard = ({ user, onLogout, currentProject }) => {
  const { projects, selectProject, projectLimit } = useProject();
  const navigate = useNavigate();
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userKeywordsCount, setUserKeywordsCount] = useState(0);
  
  // Use the effective project limit from ProjectContext/backend
  const effectiveLimit = typeof projectLimit === 'number' ? projectLimit : null;
  const hasRestrictedProjects = effectiveLimit !== null && projects.length > effectiveLimit;

  // Helper function to get cache key for keywords (matches KeywordAnalysis.js)
  const getKeywordCacheKey = (projectUrl) => {
    if (!projectUrl) return null;
    const normalizedUrl = projectUrl.toLowerCase().trim().replace(/\/$/, '');
    return `keywordAnalyses_${normalizedUrl}`;
  };

  // Load user-added keywords count from Keyword Analysis
  useEffect(() => {
    if (!currentProject?.url) {
      setUserKeywordsCount(0);
      return;
    }

    const cacheKey = getKeywordCacheKey(currentProject.url);
    if (cacheKey) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const keywords = parsed.data || [];
          setUserKeywordsCount(keywords.length);
        } else {
          setUserKeywordsCount(0);
        }
      } catch (e) {
        setUserKeywordsCount(0);
      }
    }
  }, [currentProject?.url]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.project-url-dropdown-wrapper')) {
        setIsProjectDropdownOpen(false);
      }
    };

    if (isProjectDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectDropdownOpen]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('light');
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [funFact, setFunFact] = useState('');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // eslint-disable-next-line no-unused-vars
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const cacheKey = currentProject?.url ? `dashboard_summary_${currentProject.url}` : null;
  const countdownKey = currentProject?.url ? `dashboard_countdown_${currentProject.url}` : null;

  // Check countdown on mount and when project changes
  useEffect(() => {
    if (!countdownKey) return;

    const savedCountdownEnd = localStorage.getItem(countdownKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setCountdown(remaining);
      } else {
        localStorage.removeItem(countdownKey);
        setCountdown(0);
      }
    }
  }, [countdownKey]);

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownKey) {
            localStorage.removeItem(countdownKey);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown, countdownKey]);

  const fetchSummary = useCallback(async () => {
    if (!currentProject?.url) return;
    if (countdown > 0) return; // Prevent refresh during countdown

    try {
      // Select a random fun fact
      const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
      setFunFact(randomFact);
      
      setLoading(true);
      setShowLoadingScreen(true);
      setError(null);
      
      const response = await axios.post(getApiUrl('analyze'), {
        url: currentProject.url
      }, axiosConfig);

      const data = response.data;
      const topKeywords = (data.keywords?.topKeywords || []).slice(0, 8).map((item) => item.word);
      // Store full keyword data for the new section
      const allKeywords = data.keywords?.topKeywords || [];

      const summaryPayload = {
        title: data.meta?.title || currentProject.name,
        url: currentProject.url,
        lastCrawl: data.timestamp,
        pageCount: data.sitemaps?.pageCount || data.links?.internalList?.length || 0,
        postCount: data.sitemaps?.postCount || 0,
        sitemapCount: data.sitemaps?.sitemapUrls?.length || 0,
        keywords: topKeywords,
        allKeywords: allKeywords, // Store full keyword data with counts
      };

      setSummary(summaryPayload);
      if (cacheKey) {
        localStorage.setItem(cacheKey, JSON.stringify(summaryPayload));
      }

      // Start 2-minute countdown (120 seconds)
      const countdownEndTime = Date.now() + (2 * 60 * 1000);
      if (countdownKey) {
        localStorage.setItem(countdownKey, countdownEndTime.toString());
      }
      setCountdown(120);

      // Show loading screen for 2 seconds
      setTimeout(() => {
        setShowLoadingScreen(false);
        setLoading(false);
      }, 2000);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      
      let errorMessage = 'Failed to fetch project summary';
      
      if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        errorMessage = 'Cannot connect to server. Please make sure the backend server is running on port 5001.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The website analysis is taking longer than expected.';
      } else if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection and try again.';
      } else {
        errorMessage = err.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
      setShowLoadingScreen(false);
      setLoading(false);
    }
  }, [currentProject, cacheKey, countdownKey, countdown]);

  useEffect(() => {
    if (!currentProject?.url) {
      setSummary(null);
      return;
    }

    if (cacheKey) {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          setSummary(JSON.parse(cached));
        } catch (e) {
          localStorage.removeItem(cacheKey);
          setSummary(null);
        }
      } else {
        setSummary(null);
      }
    }
  }, [currentProject, cacheKey]);

  // eslint-disable-next-line no-unused-vars
  const handleLogoutClick = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const header = (
    <header className="header">
        <div className="header-container">
          <div className="header-actions">
            <a href="/pricing" className="header-menu-item">
              Plans & Pricing
            </a>
            {user && (
              <div className="user-info">
                <button
                  className="user-icon-button"
                  onClick={() => {
                    window.location.href = '/dashboard/account-settings';
                  }}
                  aria-label="Account settings"
                  title="Account settings"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
  );

  if (!currentProject) {
    return (
      <div className="seo-analyzer dashboard-page">
        {header}
        <div className="container">
          <div className="dashboard-empty">
            <h2>Select or create a project to view its snapshot.</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="seo-analyzer dashboard-page">
      {header}

      {/* Loading Screen Overlay */}
      {showLoadingScreen && (
        <div className="page-loader-overlay">
          <div className="page-loader-content">
            {funFact && (
              <div className="fun-fact-container">
                <div className="fun-fact-icon">💡</div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
            <div className="page-loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="page-loader-text">Refreshing...</p>
            <p className="page-loader-message">
              This will take a few seconds, depends on the website size. Kindly wait until our algorithms analyze your website.
            </p>
          </div>
        </div>
      )}

      <div className="container">
        <div className="site-audit-header">
          <div className="site-audit-title">
            <span className="site-audit-label">Dashboard</span>
            <div className="project-url-dropdown-wrapper">
              <button
                className="project-url-dropdown"
                onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
              >
                <span className="project-url-text">{currentProject?.url || 'Select Project'}</span>
                <svg
                  className={`project-url-arrow ${isProjectDropdownOpen ? 'open' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {isProjectDropdownOpen && (
                <div className="project-url-dropdown-menu">
                  {projects.length > 0 ? (
                    projects.map((project, index) => {
                      // Check if this project is restricted based on effective project limit
                      const isRestricted = hasRestrictedProjects && effectiveLimit !== null && index >= effectiveLimit;
                      
                      return (
                        <button
                          key={project.id}
                          className={`project-url-dropdown-item ${
                            currentProject?.id === project.id ? 'active' : ''
                          } ${isRestricted ? 'restricted disabled' : ''}`}
                          onClick={() => {
                            if (isRestricted) {
                              setShowUpgradeModal(true);
                              setIsProjectDropdownOpen(false);
                              return;
                            }
                            selectProject(project);
                            setIsProjectDropdownOpen(false);
                          }}
                          disabled={isRestricted}
                          title={isRestricted ? 'Upgrade to access this project' : ''}
                        >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                          <span className="project-url-item-name">{project.name}</span>
                          <span className="project-url-item-url">{project.url}</span>
                        </div>
                        {isRestricted && (
                          <span className="project-restricted-badge">Upgrade Required</span>
                        )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="project-url-dropdown-empty">No projects available</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="site-audit-right">
            {summary?.lastCrawl && (
              <div className="last-crawl-info">
                <div className="last-crawl-label">Last Snapshot:</div>
                <div className="last-crawl-date">
                  {new Date(summary.lastCrawl).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}, {new Date(summary.lastCrawl).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            )}
            <div className="site-audit-actions">
              <button
                className="recrawl-btn"
                onClick={fetchSummary}
                disabled={loading || countdown > 0}
              >
                {loading ? 'Refreshing...' : countdown > 0 ? `REFRESH (${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')})` : 'REFRESH'}
              </button>
            </div>
          </div>
        </div>

        {error && <div className="dashboard-error">{error}</div>}

        {summary ? (
          <div className="dashboard-metrics-grid">
            <div className="dashboard-metric-card">
              <div className="metric-content">
                <p className="metric-label">Total Pages</p>
                <p className="metric-value">{summary.pageCount ?? '—'}</p>
              </div>
              <div className="metric-decoration">
                <img 
                  src="https://cshmhfxwounurylautnc.supabase.co/storage/v1/object/public/scanmaster-logo/Parrot%20SEO%20Elements.png" 
                  alt="Decorative illustration" 
                  className="metric-image"
                />
              </div>
            </div>
            
            <div className="dashboard-metric-card">
              <div className="metric-content">
                <p className="metric-label">Sitemaps</p>
                <p className="metric-value">{summary.sitemapCount ?? '—'}</p>
              </div>
            </div>
            
            <div className="dashboard-metric-card keywords-card">
              <div className="metric-content">
                <p className="metric-label">Keywords</p>
                <p className="metric-value">{userKeywordsCount}</p>
              </div>
              <div className="metric-decoration">
                <img 
                  src="https://cshmhfxwounurylautnc.supabase.co/storage/v1/object/public/scanmaster-logo/Parrot%20SEO%20Elements%20(1).png" 
                  alt="Parrot illustration" 
                  className="metric-image"
                />
              </div>
              <button
                className="add-keyword-btn"
                onClick={() => navigate('/dashboard/keyword-analysis')}
                title="Add Keyword"
                aria-label="Add Keyword"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          <div className="dashboard-empty compact">
            <p>No snapshot yet. Click "Refresh Snapshot" to generate one.</p>
          </div>
        )}

        {/* Most Commonly Found Words Section */}
        {summary && summary.allKeywords && summary.allKeywords.length > 0 && (
          <div className="common-words-section">
            <h2 className="common-words-title">Most commonly found words in Your Website</h2>
            <p className="common-words-disclaimer">
              This may not be your actual focus keywords but these are commonly found words which was auto-detected by our system and describes what your site is mostly about. You can ignore this if its not relevant and use our Keyword Analysis tool for a better Page + Keyword analysis.
            </p>
            <div className="common-words-list">
              {summary.allKeywords.map((keyword, index) => (
                <span key={index} className="common-word-chip">
                  {keyword.word} {keyword.count && <span className="word-count">({keyword.count})</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer');
        }}
      />
    </div>
  );
};

export default Dashboard;

